import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getTerminalInformationCode, getTerminalInformationCodeResponse } from "./TerminalManagementData/GetTerminalInformationCode";

const languageMap = {
    curl: 'bash',
    java: 'java',
    "C#": 'csharp',
    python: 'python',
    JavaScript: 'javascript'
};

const GetTerminalInformationComponent = () => {
    const [selectedLanguageGetTerminalInformation, setSelectedLanguageGetTerminalInformation] = useState('curl');
    const [copyStatus, setCopyStatus] = useState({ type: '', isCopied: false });

    const handleSelectedCode = (language) => {
        setSelectedLanguageGetTerminalInformation(language);
    };

    const handleCopyClick = (type) => {
        const textToCopy = type === 'getTerminalInformation' ? getTerminalInformationCode[selectedLanguageGetTerminalInformation].request : getTerminalInformationCodeResponse;
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                setCopyStatus({ type, isCopied: true });
                setTimeout(() => {
                    setCopyStatus({ type: '', isCopied: false });
                }, 1000);
            })
            .catch((error) => {
                console.error('Error copying text: ', error);
                alert('Failed to copy text. Please try again.');
            });
    };


    return (
        <section className="docs-section" id="terminal-info">
            <div className="row flexbox">
                <div className="col-md-5">
                    <h2 className="section-heading">Get Terminal Information</h2>
                    <p><span className="badge badge-secondary back-green">POST</span><span className="api"> /getTerminalInfo</span></p>
                    <p>The Request that allows you to get information about your terminal population. The data provided can be used to identify what terminals a customer have access to and the status.</p>

                    <h4>Request Body</h4>

                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">pageNumber</span> &nbsp; &nbsp; <b>number</b></p>
                        <p className="description">Page number for pagination, default value is <b>1</b>. <br />
                            This parameter specifies which page of results to return.</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">pageSize</span> &nbsp; &nbsp; <b>number</b></p>
                        <p className="description">Number of items per page for pagination, default value is <b>10</b>. <br />
                            This parameter specifies how many items to return in each page of results.</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">customerId</span> &nbsp; &nbsp; <b>number</b></p>
                        <p className="description">Customer ID - Identifies the customer within the Access system. <br />
                            This id can be found in the URL of each customer that you have access to. <i>/OnBoarding/ModifyCustomer?id=%</i></p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">storeId</span> &nbsp; &nbsp; <b>string</b></p>
                        <p className="description">Store ID - A custom identifier that is defined by you during the onboarding process for each customer within the Access system. This id could be a unique store number or a license code used by your system. </p></div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">model</span> &nbsp; &nbsp; <b>string</b></p>
                        <p className="description">Model - Represents the model name of the terminal (e.g., C100).</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">isActive</span> &nbsp; &nbsp; <b>boolean</b></p>
                        <p className="description">Active Status - Indicates whether the item is active or not.</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">lastTransactionTime</span> &nbsp; &nbsp; <b>string (ISO 8601 date)</b></p>
                        <p className="description">Last Transaction Time - Represents the date and time of the last transaction performed by a linked terminal-id.</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p><span className="badge badge-secondary name">firstTransactionTime</span> &nbsp; &nbsp; <b>string (ISO 8601 date)</b></p>
                        <p className="description">First Transaction Time - Represents the date and time of the first transaction registered for a terminal-id linked to a terminal serial number.</p>
                    </div>
                    <br />

                    <div className="response-codes">

                        <h4>Response Codes</h4>

                        <div className="table-wrapper">

                            <h6 className="header">HTTP Status code summary</h6>

                            <table>
                                <tbody>
                                    <tr>
                                        <td>200 Success</td>
                                        <td>Get terminalid request was successful.</td>
                                    </tr>
                                    <tr>
                                        <td>400 Bad Request</td>
                                        <td>Bad request format or validation failed for incorrect data formats.</td>
                                    </tr>
                                    <tr>
                                        <td>401 Unauthorized</td>
                                        <td>Request requires authentication, and the provided credentials are either missing, invalid, or insufficient, hence send a valid merchant access token (Bearer) issued by Westpay in the "Authorization" header.</td>
                                    </tr>
                                    <tr>
                                        <td>404 Resources not found</td>
                                        <td>If request cannot find data for provided data.</td>
                                    </tr>
                                    <tr>
                                        <td>422 Unprocessable entity</td>
                                        <td>Indicates that the server understands the request but cannot process it due to semantic errors or invalid data in the request payload.</td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>

                    </div>

                </div>

                <div className="col-md-7 code-segments">
                    <div className="docs-code-block">
                        <pre tabIndex="-1" className="shadow-lg rounded">
                            <code className="language-dockerfile hljs">
                                <div className="row code-header">
                                    <div className="col-md-10 languages">
                                        <span>Request</span>
                                        {Object.keys(getTerminalInformationCode).map(language => (
                                            <React.Fragment key={language}>
                                                <input type="radio" name="langGetTerminalInformation" id={`${language}GetTerminalInformation`} onClick={() => handleSelectedCode(language)} checked={selectedLanguageGetTerminalInformation === language} />
                                                <label htmlFor={`${language}GetTerminalInformation`} className="language">{language}</label>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <div className="col-md-2">
                                        {copyStatus.type !== 'getTerminalInformation' && <span className="copy-btn" title="Copy" onClick={() => handleCopyClick("getTerminalInformation")}><i className="fa fa-copy"></i></span>}
                                        {copyStatus.type === 'getTerminalInformation' && copyStatus.isCopied && <div className="copy-message">Copied &#x2713;</div>}
                                    </div>
                                </div>
                                <div className="code-section">
                                    <SyntaxHighlighter language={languageMap[selectedLanguageGetTerminalInformation]} style={dracula} showLineNumbers>
                                        {getTerminalInformationCode[selectedLanguageGetTerminalInformation].request}
                                    </SyntaxHighlighter>
                                </div>
                            </code>
                        </pre>
                    </div>
                    <div className="docs-code-block">
                        <pre tabIndex="-1" className="shadow-lg rounded">
                            <code className="language-dockerfile hljs">
                                <div className="row code-header">
                                    <div className="col-md-10 languages">
                                        <span>Response</span>
                                    </div>
                                    <div className="col-md-2">
                                        {copyStatus.type !== 'getTerminalInformationResponse' && <span className="copy-btn" title="Copy" onClick={() => handleCopyClick("getTerminalInformationResponse")}><i className="fa fa-copy"></i></span>}
                                        {copyStatus.type === 'getTerminalInformationResponse' && copyStatus.isCopied && <div className="copy-message">Copied &#x2713;</div>}
                                    </div>
                                </div>
                                <div className="code-section">
                                    <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                        {getTerminalInformationCodeResponse}
                                    </SyntaxHighlighter>
                                </div>
                            </code>
                        </pre>
                    </div>
                </div>


            </div>
        </section>
    );
};

export default GetTerminalInformationComponent;